/**
 * EmployeeInvitePage — öffentliche Setup-Seite für die Mitarbeiter-Einladung
 * (Jul-2026, User-Wunsch). Ein Mitarbeiter *ohne Login* mit privater E-Mail
 * richtet hier seinen persönlichen Zugang (Verkäufer/Vermittler) ein.
 * Route: /mitarbeiter-einladung/:token
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertCircle, UserPlus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmployeeInvitePage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [invite, setInvite] = useState(null);
    const [error, setError] = useState("");
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/public/employee-invite/${token}`);
                setInvite(data);
            } catch (e) {
                setError(e.response?.data?.detail || "Einladung ungültig oder abgelaufen.");
            } finally { setLoading(false); }
        })();
    }, [token]);

    const submit = async () => {
        if (pw.length < 6) { toast.error("Passwort zu kurz (min. 6 Zeichen)"); return; }
        if (pw !== pw2) { toast.error("Passwörter stimmen nicht überein"); return; }
        setSaving(true);
        try {
            await axios.post(`${API}/public/employee-invite/${token}/accept`, { password: pw });
            setDone(true);
            toast.success("Zugang erfolgreich eingerichtet!");
            setTimeout(() => navigate("/login"), 2500);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Einrichtung fehlgeschlagen");
        } finally { setSaving(false); }
    };

    return (
        <div className="min-h-screen bg-[#0E0E10] flex items-center justify-center px-4" data-testid="employee-invite-page">
            <div className="w-full max-w-md bg-white rounded-sm border-2 border-[#FFCC00] shadow-xl p-6 md:p-8">
                <div className="flex items-center gap-2 mb-5">
                    <Logo className="inline-flex items-center gap-2" boxClass="w-9 h-9" iconClass="w-5 h-5" textClass="text-2xl" deClass="text-gray-400" />
                </div>

                {loading ? (
                    <div className="py-12 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Einladung wird geladen…</div>
                ) : error ? (
                    <div className="py-8 text-center" data-testid="invite-error">
                        <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                        <p className="font-bold text-[#111827]">Einladung nicht verfügbar</p>
                        <p className="text-sm text-gray-600 mt-1">{error}</p>
                        <Button onClick={() => navigate("/login")} className="mt-4 bg-[#111827] text-white rounded-sm" data-testid="invite-goto-login">Zum Login</Button>
                    </div>
                ) : done ? (
                    <div className="py-8 text-center" data-testid="invite-done">
                        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                        <p className="font-bold text-[#111827] text-lg">Zugang eingerichtet!</p>
                        <p className="text-sm text-gray-600 mt-1">Sie können sich jetzt mit <strong>{invite?.email}</strong> anmelden. Weiterleitung…</p>
                    </div>
                ) : (
                    <>
                        <div className="flex items-center gap-2 mb-2">
                            <UserPlus className="w-5 h-5 text-[#FF8300]" />
                            <h1 className="font-display font-black text-2xl text-[#111827]">Persönlichen Zugang einrichten</h1>
                        </div>
                        <p className="text-sm text-gray-600 mb-4">
                            <strong>{invite?.dealer_company}</strong> hat für Sie einen Zugang als{" "}
                            <span className="font-bold text-[#111827]">{invite?.role_label}</span> vorbereitet. Bitte vergeben Sie Ihr eigenes Passwort.
                        </p>
                        <div className="space-y-3">
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-600">Login-E-Mail</Label>
                                <Input value={invite?.email || ""} disabled className="h-10 bg-gray-100" data-testid="invite-email" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-600">Passwort</Label>
                                <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Mindestens 6 Zeichen" className="h-10" data-testid="invite-password" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase tracking-wider text-gray-600">Passwort wiederholen</Label>
                                <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Passwort erneut eingeben" className="h-10" data-testid="invite-password-confirm" />
                            </div>
                            <Button onClick={submit} disabled={saving} className="w-full bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black uppercase tracking-wider rounded-sm h-11" data-testid="invite-submit">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShieldCheck className="w-4 h-4 mr-1" /> Zugang aktivieren</>}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
